/**
 * ChecklistsDashboard.js — Autodesk Build-style checklists & templates
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

const API = '/technical-checklists';

const CHECKLIST_TYPES = ['Quality', 'Safety', 'Punch List', 'Commissioning'];

const STATUS_BADGE = {
  'Not Started': '#9ca3af',
  'In Progress': '#f59e0b',
  Completed: '#16a34a',
  'On Hold': '#6b7280',
};

const TYPE_STYLE = {
  Quality: { color: '#3b82f6', label: 'Quality', icon: '◆' },
  Safety: { color: '#ef4444', label: 'Safety', icon: '⚠' },
  'Punch List': { color: '#f59e0b', label: 'Punch List', icon: '☰' },
  Commissioning: { color: '#8b5cf6', label: 'Commissioning', icon: '⚡' },
};

/** Group key when checklist item.section is null/empty */
const NO_SECTION = '(No section)';

const baseFont = { fontFamily: 'system-ui, sans-serif' };

function parseSections(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw || '[]');
    } catch {
      return [];
    }
  }
  return [];
}

function countTemplateItems(sections) {
  const arr = parseSections(sections);
  return arr.reduce((sum, sec) => sum + (Array.isArray(sec?.items) ? sec.items.length : 0), 0);
}

function groupItemsBySection(items) {
  const map = new Map();
  for (const it of items || []) {
    const key = it.section && String(it.section).trim() ? String(it.section).trim() : NO_SECTION;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  const pairs = Array.from(map.entries());
  pairs.sort(([a], [b]) => a.localeCompare(b));
  for (const [, list] of pairs) {
    list.sort((x, y) => (x.item_number || 0) - (y.item_number || 0));
  }
  return pairs;
}

function normalizeAnswerType(t) {
  const s = String(t || 'checkbox').toLowerCase().replace(/\s+/g, '_');
  if (s.includes('pass') && s.includes('fail')) return 'pass_fail';
  if (s === 'pass_fail' || s === 'pass-fail') return 'pass_fail';
  if (s === 'text') return 'text';
  return 'checkbox';
}

const ChecklistsDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalChecklists', projectId || 'global', true);

  const userLabel = useMemo(() => {
    const a = accounts[0];
    if (!a) return 'User';
    return (
      a.name ||
      a.idTokenClaims?.name ||
      a.username ||
      a.idTokenClaims?.preferred_username ||
      'User'
    );
  }, [accounts]);

  const [activeTab, setActiveTab] = useState('checklists');

  const [checklists, setChecklists] = useState([]);
  const [loadingChecklists, setLoadingChecklists] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    checklist_type: 'Quality',
    description: '',
    location: '',
    assigned_to: '',
    due_date: '',
    template_id: '',
  });
  const [creating, setCreating] = useState(false);

  const [detailId, setDetailId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [itemDrafts, setItemDrafts] = useState({});
  const [addItemForms, setAddItemForms] = useState({});

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    checklist_type: 'Quality',
    description: '',
    sections: [{ name: 'General', items: [{ description: '', answer_type: 'checkbox' }] }],
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  const fetchChecklists = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingChecklists(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}`);
      if (r.data?.success) setChecklists(r.data.data || []);
    } catch (err) {
      console.error('Fetch checklists error:', err);
      showToast('Failed to load checklists', 'error');
    } finally {
      setLoadingChecklists(false);
    }
  }, [instance, accounts, projectId]);

  const fetchTemplates = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingTemplates(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/templates`);
      if (r.data?.success) setTemplates(r.data.data || []);
    } catch (err) {
      console.error('Fetch templates error:', err);
      showToast('Failed to load templates', 'error');
    } finally {
      setLoadingTemplates(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  useEffect(() => {
    if (activeTab === 'templates') fetchTemplates();
  }, [activeTab, fetchTemplates]);

  useEffect(() => {
    if (showCreateModal && projectId) fetchTemplates();
  }, [showCreateModal, projectId, fetchTemplates]);

  const loadChecklistDetail = useCallback(
    async (checklistId) => {
      if (!checklistId) return;
      try {
        setLoadingDetail(true);
        const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/checklist/${checklistId}`);
        if (r.data?.success) {
          const data = r.data.data;
          setDetailData(data);
          const drafts = {};
          for (const it of data.items || []) {
            drafts[it.id] = {
              notes: it.notes || '',
              answer: it.answer || '',
            };
          }
          setItemDrafts(drafts);

          const items = data.items || [];
          if (items.length) {
            const allDone = items.every((i) => String(i.status || '') === 'Completed');
            const anyDone = items.some((i) => String(i.status || '') === 'Completed');
            const st = String(data.status || '');
            if (allDone && st !== 'Completed') {
              const r2 = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/checklist/${checklistId}`, { status: 'Completed' });
              if (r2.data?.success) {
                setDetailData((d) => (d ? { ...d, ...r2.data.data } : d));
                fetchChecklists();
              }
            } else if (!allDone && anyDone && st === 'Not Started') {
              const r2 = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/checklist/${checklistId}`, { status: 'In Progress' });
              if (r2.data?.success) {
                setDetailData((d) => (d ? { ...d, ...r2.data.data } : d));
                fetchChecklists();
              }
            }
          }
        }
      } catch (err) {
        console.error('Load checklist detail error:', err);
        showToast('Failed to load checklist', 'error');
      } finally {
        setLoadingDetail(false);
      }
    },
    [instance, accounts, fetchChecklists]
  );

  useEffect(() => {
    if (detailId) loadChecklistDetail(detailId);
    else {
      setDetailData(null);
      setItemDrafts({});
      setAddItemForms({});
    }
  }, [detailId, loadChecklistDetail]);

  const filteredChecklists = useMemo(() => {
    let rows = checklists || [];
    if (typeFilter !== 'All') {
      rows = rows.filter((c) => (c.checklist_type || '') === typeFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((c) => {
        const blob = [c.name, c.location, c.assigned_to, c.description, c.status].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    return rows;
  }, [checklists, search, typeFilter]);

  const handleCreateChecklist = async () => {
    if (!createForm.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setCreating(true);
    try {
      const body = {
        name: createForm.name.trim(),
        checklist_type: createForm.checklist_type,
        description: createForm.description || null,
        location: createForm.location || null,
        assigned_to: createForm.assigned_to || null,
        due_date: createForm.due_date || null,
      };
      if (createForm.template_id) {
        body.template_id = parseInt(createForm.template_id, 10);
      }
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}`, body);
      if (r.data?.success) {
        setShowCreateModal(false);
        setCreateForm({
          name: '',
          checklist_type: 'Quality',
          description: '',
          location: '',
          assigned_to: '',
          due_date: '',
          template_id: '',
        });
        fetchChecklists();
        showToast('Checklist created');
      } else {
        showToast(r.data?.message || 'Create failed', 'error');
      }
    } catch (err) {
      showToast('Failed to create checklist', 'error');
    } finally {
      setCreating(false);
    }
  };

  const updateItem = async (itemId, payload) => {
    try {
      const r = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/items/${itemId}`, payload);
      if (r.data?.success) {
        await loadChecklistDetail(detailId);
        fetchChecklists();
        return true;
      }
      showToast(r.data?.message || 'Update failed', 'error');
      return false;
    } catch (err) {
      showToast('Failed to update item', 'error');
      return false;
    }
  };

  const handleCheckboxToggle = async (item, checked) => {
    if (checked) {
      await updateItem(item.id, {
        answer: 'Yes',
        status: 'Completed',
        checked_by: userLabel,
        checked_at: new Date().toISOString(),
      });
    } else {
      await updateItem(item.id, {
        answer: null,
        status: 'Pending',
        checked_by: null,
        checked_at: null,
      });
    }
  };

  const handlePassFail = async (item, value) => {
    const completed = value === 'Pass' || value === 'Fail';
    await updateItem(item.id, {
      answer: value,
      status: completed ? 'Completed' : 'Pending',
      checked_by: completed ? userLabel : null,
      checked_at: completed ? new Date().toISOString() : null,
    });
  };

  const flushTextAnswer = async (item) => {
    const draft = itemDrafts[item.id] || {};
    const text = (draft.answer ?? '').trim();
    const completed = text.length > 0;
    await updateItem(item.id, {
      answer: text || null,
      status: completed ? 'Completed' : 'Pending',
      checked_by: completed ? userLabel : null,
      checked_at: completed ? new Date().toISOString() : null,
    });
  };

  const saveNotes = async (item) => {
    const notes = (itemDrafts[item.id]?.notes ?? '').trim();
    await updateItem(item.id, { notes: notes || null });
  };

  const updateChecklistMeta = async (patch) => {
    if (!detailId) return;
    try {
      const r = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/checklist/${detailId}`, patch);
      if (r.data?.success) {
        setDetailData((d) => (d ? { ...d, ...r.data.data } : d));
        fetchChecklists();
      }
    } catch (err) {
      showToast('Failed to update checklist', 'error');
    }
  };

  const handleAddItem = async (sectionKey) => {
    const form = addItemForms[sectionKey] || { description: '', answer_type: 'checkbox' };
    if (!form.description.trim()) {
      showToast('Description is required', 'error');
      return;
    }
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/checklist/${detailId}/items`, {
        description: form.description.trim(),
        section: sectionKey === NO_SECTION ? null : sectionKey,
        answer_type: form.answer_type,
      });
      if (r.data?.success) {
        setAddItemForms((prev) => ({ ...prev, [sectionKey]: { description: '', answer_type: 'checkbox' } }));
        await loadChecklistDetail(detailId);
        fetchChecklists();
        showToast('Item added');
      }
    } catch (err) {
      showToast('Failed to add item', 'error');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/items/${itemId}`);
      await loadChecklistDetail(detailId);
      fetchChecklists();
      showToast('Item deleted');
    } catch (err) {
      showToast('Failed to delete item', 'error');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      showToast('Template name is required', 'error');
      return;
    }
    const sections = (templateForm.sections || [])
      .map((sec) => ({
        name: (sec.name || '').trim() || 'Section',
        items: (sec.items || [])
          .filter((it) => (it.description || '').trim())
          .map((it) => ({
            description: it.description.trim(),
            answer_type: it.answer_type || 'checkbox',
          })),
      }))
      .filter((sec) => sec.items.length > 0);
    if (!sections.length) {
      showToast('Add at least one item in a section', 'error');
      return;
    }
    setSavingTemplate(true);
    try {
      const body = {
        name: templateForm.name.trim(),
        checklist_type: templateForm.checklist_type,
        description: templateForm.description || null,
        sections,
      };
      if (editingTemplateId) {
        const r = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/templates/${editingTemplateId}`, body);
        if (r.data?.success) {
          showToast('Template updated');
          setShowTemplateModal(false);
          setEditingTemplateId(null);
          fetchTemplates();
        } else showToast(r.data?.message || 'Save failed', 'error');
      } else {
        const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/templates`, body);
        if (r.data?.success) {
          showToast('Template created');
          setShowTemplateModal(false);
          fetchTemplates();
        } else showToast(r.data?.message || 'Save failed', 'error');
      }
    } catch (err) {
      showToast('Failed to save template', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const openNewTemplate = () => {
    setEditingTemplateId(null);
    setTemplateForm({
      name: '',
      checklist_type: 'Quality',
      description: '',
      sections: [{ name: 'General', items: [{ description: '', answer_type: 'checkbox' }] }],
    });
    setShowTemplateModal(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplateId(t.id);
    const secs = parseSections(t.sections);
    const normalized =
      secs.length > 0
        ? secs.map((s) => ({
            name: s.name || 'Section',
            items:
              Array.isArray(s.items) && s.items.length
                ? s.items.map((it) => ({
                    description: it.description || '',
                    answer_type: it.answer_type || 'checkbox',
                  }))
                : [{ description: '', answer_type: 'checkbox' }],
          }))
        : [{ name: 'General', items: [{ description: '', answer_type: 'checkbox' }] }];
    setTemplateForm({
      name: t.name || '',
      checklist_type: t.checklist_type || 'Quality',
      description: t.description || '',
      sections: normalized,
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/templates/${id}`);
      fetchTemplates();
      showToast('Template deleted');
    } catch (err) {
      showToast('Failed to delete template', 'error');
    }
  };

  const renderTypeBadge = (type) => {
    const t = TYPE_STYLE[type] || { color: '#6b7280', label: type || '—', icon: '' };
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 8px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          color: '#fff',
          background: t.color,
        }}
      >
        {t.icon ? <span aria-hidden="true">{t.icon}</span> : null}
        {t.label}
      </span>
    );
  };

  const renderStatusBadge = (status) => {
    const s = status || 'Not Started';
    const bg = STATUS_BADGE[s] || '#9ca3af';
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          color: '#fff',
          background: bg,
        }}
      >
        {s}
      </span>
    );
  };

  const progressBar = (completed, total) => {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return (
      <div style={{ width: '100%', maxWidth: 160 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
          <span>{completed}/{total}</span>
          <span>{pct}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: '#e5e7eb', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: '#16a34a', transition: 'width 0.2s' }} />
        </div>
      </div>
    );
  };

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
  };

  const modalBox = {
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    maxWidth: 520,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    ...baseFont,
  };

  const detailModalBox = {
    ...modalBox,
    maxWidth: 880,
  };

  return (
    <div style={{ ...baseFont, background: '#fff', minHeight: 320 }}>
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '12px 16px',
            borderRadius: 8,
            background: toast.type === 'error' ? '#dc2626' : '#16a34a',
            color: '#fff',
            zIndex: 2000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontSize: 14,
          }}
        >
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 16, alignItems: 'center' }}>
        <div style={{ display: 'flex', flex: 1 }}>
          {['checklists', 'templates'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: activeTab === tab ? '#fff' : 'transparent',
                borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                color: activeTab === tab ? '#2563eb' : '#6b7280',
                fontWeight: 600,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {tab}
            </button>
          ))}
        </div>
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
      </div>

      {activeTab === 'checklists' && (
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
            <input
              type="search"
              placeholder="Search checklists..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: '1 1 200px',
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                fontSize: 14,
              }}
            />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14 }}
            >
              <option value="All">All types</option>
              {CHECKLIST_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '8px 16px',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Create Checklist
            </button>
          </div>

          {loadingChecklists ? (
            <div style={{ padding: 24, color: '#6b7280' }}>Loading…</div>
          ) : (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                    {['Name', 'Type', 'Status', 'Location', 'Assigned To', 'Due Date', 'Progress'].map((h) => (
                      <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredChecklists.map((c) => {
                    const total = c.item_count_total ?? 0;
                    const done = c.item_count_completed ?? 0;
                    return (
                      <tr
                        key={c.id}
                        onClick={() => setDetailId(c.id)}
                        style={{ cursor: 'pointer', borderBottom: '1px solid #e5e7eb' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff';
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: 500, color: '#111827' }}>{c.name}</td>
                        <td style={{ padding: '12px' }}>{renderTypeBadge(c.checklist_type)}</td>
                        <td style={{ padding: '12px' }}>{renderStatusBadge(c.status)}</td>
                        <td style={{ padding: '12px', color: '#4b5563' }}>{c.location || '—'}</td>
                        <td style={{ padding: '12px', color: '#4b5563' }}>{c.assigned_to || '—'}</td>
                        <td style={{ padding: '12px', color: '#4b5563' }}>
                          {c.due_date ? String(c.due_date).slice(0, 10) : '—'}
                        </td>
                        <td style={{ padding: '12px' }}>{progressBar(done, total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!filteredChecklists.length && (
                <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>No checklists match your filters.</div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={openNewTemplate}
              style={{
                padding: '8px 16px',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Create Template
            </button>
          </div>
          {loadingTemplates ? (
            <div style={{ color: '#6b7280' }}>Loading templates…</div>
          ) : (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                    {['Name', 'Type', 'Items', 'Actions'].map((h) => (
                      <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td
                        style={{ padding: '12px', fontWeight: 500 }}
                        onMouseEnter={(e) => {
                          e.currentTarget.parentElement.style.background = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.parentElement.style.background = '#fff';
                        }}
                      >
                        {t.name}
                      </td>
                      <td style={{ padding: '12px' }}>{renderTypeBadge(t.checklist_type)}</td>
                      <td style={{ padding: '12px', color: '#4b5563' }}>{countTemplateItems(t.sections)}</td>
                      <td style={{ padding: '12px' }}>
                        <button
                          type="button"
                          onClick={() => openEditTemplate(t)}
                          style={{ marginRight: 8, padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteTemplate(t.id)}
                          style={{ padding: '4px 10px', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!templates.length && <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>No templates yet.</div>}
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <div style={overlayStyle} onClick={() => !creating && setShowCreateModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 16 }}>Create Checklist</div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Name *
                <input
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Type
                <select
                  value={createForm.checklist_type}
                  onChange={(e) => setCreateForm((f) => ({ ...f, checklist_type: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                >
                  {CHECKLIST_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Create from template (optional)
                <select
                  value={createForm.template_id}
                  onChange={(e) => setCreateForm((f) => ({ ...f, template_id: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                >
                  <option value="">— None —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({countTemplateItems(t.sections)} items)
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Description
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6, resize: 'vertical' }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Location
                <input
                  value={createForm.location}
                  onChange={(e) => setCreateForm((f) => ({ ...f, location: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Assigned to
                <input
                  value={createForm.assigned_to}
                  onChange={(e) => setCreateForm((f) => ({ ...f, assigned_to: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Due date
                <input
                  type="date"
                  value={createForm.due_date}
                  onChange={(e) => setCreateForm((f) => ({ ...f, due_date: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                />
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={() => setShowCreateModal(false)} disabled={creating} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateChecklist}
                  disabled={creating}
                  style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                >
                  {creating ? 'Creating…' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailId && (
        <div style={overlayStyle} onClick={() => setDetailId(null)}>
          <div style={detailModalBox} onClick={(e) => e.stopPropagation()}>
            {loadingDetail || !detailData ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading checklist…</div>
            ) : (
              <>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>{detailData.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {renderTypeBadge(detailData.checklist_type)}
                        <select
                          value={detailData.status || 'Not Started'}
                          onChange={(e) => updateChecklistMeta({ status: e.target.value })}
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 13 }}
                        >
                          {['Not Started', 'In Progress', 'Completed', 'On Hold'].map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button type="button" onClick={() => setDetailId(null)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>
                      ×
                    </button>
                  </div>
                  {(() => {
                    const items = detailData.items || [];
                    const total = items.length;
                    const done = items.filter((i) => String(i.status || '') === 'Completed').length;
                    return (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
                          {done} of {total} completed
                        </div>
                        {progressBar(done, total || 1)}
                      </div>
                    );
                  })()}
                </div>
                <div style={{ padding: '12px 20px 24px', maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}>
                  {groupItemsBySection(detailData.items).map(([sectionName, sectionItems]) => (
                    <div key={sectionName} style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #e5e7eb' }}>{sectionName}</div>
                      {sectionItems.map((item) => {
                        const at = normalizeAnswerType(item.answer_type);
                        const draft = itemDrafts[item.id] || { notes: item.notes || '', answer: item.answer || '' };
                        const completed = String(item.status || '') === 'Completed';
                        return (
                          <div
                            key={item.id}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: 12,
                              padding: '12px',
                              marginBottom: 8,
                              border: '1px solid #e5e7eb',
                              borderRadius: 8,
                              background: completed ? '#f0fdf4' : '#fff',
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                                <span style={{ fontSize: 12, color: '#6b7280', minWidth: 28 }}>#{item.item_number}</span>
                                <span style={{ fontSize: 14, color: '#111827', flex: 1 }}>{item.description}</span>
                                {renderStatusBadge(item.status || 'Pending')}
                              </div>
                              <div style={{ marginLeft: 36, marginBottom: 8 }}>
                                {at === 'checkbox' && (
                                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                                    <input
                                      type="checkbox"
                                      checked={completed && (item.answer === 'Yes' || !item.answer)}
                                      onChange={(e) => handleCheckboxToggle(item, e.target.checked)}
                                    />
                                    <span>Complete</span>
                                  </label>
                                )}
                                {at === 'text' && (
                                  <div>
                                    <input
                                      type="text"
                                      value={draft.answer}
                                      onChange={(e) =>
                                        setItemDrafts((prev) => ({
                                          ...prev,
                                          [item.id]: { ...draft, answer: e.target.value },
                                        }))
                                      }
                                      onBlur={() => flushTextAnswer(item)}
                                      placeholder="Enter response"
                                      style={{ width: '100%', maxWidth: 400, padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14 }}
                                    />
                                  </div>
                                )}
                                {at === 'pass_fail' && (
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                      type="button"
                                      onClick={() => handlePassFail(item, 'Pass')}
                                      style={{
                                        padding: '6px 14px',
                                        borderRadius: 6,
                                        border: item.answer === 'Pass' ? '2px solid #16a34a' : '1px solid #e5e7eb',
                                        background: item.answer === 'Pass' ? '#dcfce7' : '#fff',
                                        color: '#166534',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                      }}
                                    >
                                      Pass
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handlePassFail(item, 'Fail')}
                                      style={{
                                        padding: '6px 14px',
                                        borderRadius: 6,
                                        border: item.answer === 'Fail' ? '2px solid #dc2626' : '1px solid #e5e7eb',
                                        background: item.answer === 'Fail' ? '#fee2e2' : '#fff',
                                        color: '#991b1b',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                      }}
                                    >
                                      Fail
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div style={{ marginLeft: 36 }}>
                                <label style={{ fontSize: 12, color: '#6b7280' }}>
                                  Notes
                                  <textarea
                                    value={draft.notes}
                                    onChange={(e) =>
                                      setItemDrafts((prev) => ({
                                        ...prev,
                                        [item.id]: { ...draft, notes: e.target.value },
                                      }))
                                    }
                                    onBlur={() => saveNotes(item)}
                                    rows={2}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      maxWidth: 480,
                                      marginTop: 4,
                                      padding: 8,
                                      border: '1px solid #e5e7eb',
                                      borderRadius: 6,
                                      fontSize: 13,
                                      resize: 'vertical',
                                    }}
                                  />
                                </label>
                              </div>
                            </div>
                            <div>
                              <button
                                type="button"
                                onClick={() => handleDeleteItem(item.id)}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  color: '#dc2626',
                                  border: '1px solid #fecaca',
                                  borderRadius: 6,
                                  background: '#fff',
                                  cursor: 'pointer',
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <div
                        style={{
                          marginTop: 10,
                          padding: 12,
                          border: '1px dashed #d1d5db',
                          borderRadius: 8,
                          background: '#fafafa',
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Add item to this section</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
                          <input
                            placeholder="Description"
                            value={(addItemForms[sectionName] || {}).description || ''}
                            onChange={(e) =>
                              setAddItemForms((prev) => ({
                                ...prev,
                                [sectionName]: { ...(prev[sectionName] || { answer_type: 'checkbox' }), description: e.target.value },
                              }))
                            }
                            style={{ flex: '1 1 200px', padding: 8, border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 }}
                          />
                          <select
                            value={(addItemForms[sectionName] || {}).answer_type || 'checkbox'}
                            onChange={(e) =>
                              setAddItemForms((prev) => ({
                                ...prev,
                                [sectionName]: { ...(prev[sectionName] || { description: '' }), answer_type: e.target.value },
                              }))
                            }
                            style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 }}
                          >
                            <option value="checkbox">Checkbox</option>
                            <option value="text">Text</option>
                            <option value="pass_fail">Pass / Fail</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleAddItem(sectionName)}
                            style={{
                              padding: '8px 12px',
                              background: '#2563eb',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontSize: 13,
                            }}
                          >
                            Add Item
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!detailData.items || detailData.items.length === 0) && (
                    <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>No items yet. Add items below.</div>
                  )}
                  {(!detailData.items || detailData.items.length === 0) && (
                    <div style={{ padding: 12, border: '1px dashed #d1d5db', borderRadius: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Add first item</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <input
                          placeholder="Description"
                          value={(addItemForms[NO_SECTION] || {}).description || ''}
                          onChange={(e) =>
                            setAddItemForms((prev) => ({
                              ...prev,
                              [NO_SECTION]: { ...(prev[NO_SECTION] || { answer_type: 'checkbox' }), description: e.target.value },
                            }))
                          }
                          style={{ flex: 1, minWidth: 200, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                        />
                        <select
                          value={(addItemForms[NO_SECTION] || {}).answer_type || 'checkbox'}
                          onChange={(e) =>
                            setAddItemForms((prev) => ({
                              ...prev,
                              [NO_SECTION]: { ...(prev[NO_SECTION] || { description: '' }), answer_type: e.target.value },
                            }))
                          }
                          style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                        >
                          <option value="checkbox">Checkbox</option>
                          <option value="text">Text</option>
                          <option value="pass_fail">Pass / Fail</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => handleAddItem(NO_SECTION)}
                          style={{ padding: '8px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Add Item
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showTemplateModal && (
        <div style={overlayStyle} onClick={() => !savingTemplate && setShowTemplateModal(false)}>
          <div style={{ ...modalBox, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 16 }}>
              {editingTemplateId ? 'Edit Template' : 'Create Template'}
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label style={{ fontSize: 13 }}>
                Name *
                <input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Type
                <select
                  value={templateForm.checklist_type}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, checklist_type: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                >
                  {CHECKLIST_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                Description
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                />
              </label>

              <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4 }}>Sections & items</div>
              {(templateForm.sections || []).map((sec, si) => (
                <div key={si} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#f9fafb' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                    <input
                      placeholder="Section name"
                      value={sec.name}
                      onChange={(e) => {
                        const next = [...templateForm.sections];
                        next[si] = { ...next[si], name: e.target.value };
                        setTemplateForm((f) => ({ ...f, sections: next }));
                      }}
                      style={{ flex: 1, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = templateForm.sections.filter((_, i) => i !== si);
                        setTemplateForm((f) => ({ ...f, sections: next.length ? next : [{ name: 'General', items: [{ description: '', answer_type: 'checkbox' }] }] }));
                      }}
                      style={{ padding: '6px 10px', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12 }}
                    >
                      Remove section
                    </button>
                  </div>
                  {(sec.items || []).map((it, ii) => (
                    <div key={ii} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <input
                        placeholder="Item description"
                        value={it.description}
                        onChange={(e) => {
                          const next = [...templateForm.sections];
                          const items = [...(next[si].items || [])];
                          items[ii] = { ...items[ii], description: e.target.value };
                          next[si] = { ...next[si], items };
                          setTemplateForm((f) => ({ ...f, sections: next }));
                        }}
                        style={{ flex: '1 1 200px', padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                      />
                      <select
                        value={it.answer_type || 'checkbox'}
                        onChange={(e) => {
                          const next = [...templateForm.sections];
                          const items = [...(next[si].items || [])];
                          items[ii] = { ...items[ii], answer_type: e.target.value };
                          next[si] = { ...next[si], items };
                          setTemplateForm((f) => ({ ...f, sections: next }));
                        }}
                        style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
                      >
                        <option value="checkbox">Checkbox</option>
                        <option value="text">Text</option>
                        <option value="pass_fail">Pass / Fail</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          const next = [...templateForm.sections];
                          const items = (next[si].items || []).filter((_, i) => i !== ii);
                          next[si] = { ...next[si], items: items.length ? items : [{ description: '', answer_type: 'checkbox' }] };
                          setTemplateForm((f) => ({ ...f, sections: next }));
                        }}
                        style={{ fontSize: 12, color: '#dc2626', border: 'none', background: 'transparent', cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...templateForm.sections];
                      next[si] = { ...next[si], items: [...(next[si].items || []), { description: '', answer_type: 'checkbox' }] };
                      setTemplateForm((f) => ({ ...f, sections: next }));
                    }}
                    style={{ marginTop: 4, padding: '6px 10px', border: '1px dashed #93c5fd', color: '#2563eb', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}
                  >
                    + Add item
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setTemplateForm((f) => ({
                    ...f,
                    sections: [...(f.sections || []), { name: `Section ${(f.sections || []).length + 1}`, items: [{ description: '', answer_type: 'checkbox' }] }],
                  }))
                }
                style={{ alignSelf: 'flex-start', padding: '8px 12px', border: '1px solid #2563eb', color: '#2563eb', borderRadius: 6, background: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                + Add section
              </button>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={() => setShowTemplateModal(false)} disabled={savingTemplate} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate}
                  style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                >
                  {savingTemplate ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistsDashboard;
